// Share panel — sits below the appointment list.
// Bietet zwei Modi:
//   1. CREATE — noch keine Umfrage erstellt. Button: „Umfrage erstellen & teilen"
//   2. EDIT   — Umfrage existiert bereits (per current-build im localStorage).
//      Button: „✏ Änderungen übernehmen" — PATCHt die bestehende Umfrage,
//      ohne neue Tokens zu generieren. Schon verschickte Voter-Links bleiben.
//
// Reset-Link erlaubt jederzeit den Sprung zurück in den CREATE-Modus für
// eine komplett neue Umfrage.

import type { Store } from '../store';
import {
  ApiError, createSurvey, getAdminSurvey, updateSurvey, type VoterVisibility,
} from '../api';
import { showCreateModal } from './create-modal';
import { addSurvey } from '../admin-store';
import {
  clearCurrentBuild, getCurrentBuild, setCurrentBuild, type CurrentBuild,
} from '../current-build';
import { openDateRangePicker } from './date-range-picker';
import { formatDateShort } from '../time';
import { el } from './dom';

export function renderSharePanel(store: Store): HTMLElement {
  const root = el('section', { class: 'share-panel', 'aria-label': 'Umfrage teilen' });

  const heading = el('h2', { class: 'panel-heading' }, 'Teilen');
  const intro = el(
    'p',
    { class: 'share-intro' },
    'Wenn deine Terminliste bereit ist, erstelle eine Umfrage. ',
    'Du erhältst einen Teilnehmer-Link zum Versenden und eine Privacy-Datei mit deinem Admin-Zugang.',
  );
  const privacyNote = el(
    'p',
    { class: 'share-privacy' },
    '🔒 ',
    el('strong', {}, 'Privacy-First:'),
    ' Wir speichern nur Termine und (anonyme) Antworten — keine Accounts, keine E-Mail-Adressen, kein Tracking. ',
    'Wer den Admin-Link verliert, hat keinen Zugang mehr — ',
    el('em', {}, 'bewahre die Privacy-Datei sicher auf.'),
  );

  const titleInput = el('input', {
    type: 'text',
    class: 'share-title-input',
    placeholder: 'Titel der Umfrage (optional)',
    maxlength: 200,
  }) as HTMLInputElement;

  const locationInput = el('input', {
    type: 'text',
    class: 'share-location-input',
    placeholder: '📍 Ort oder Link',
    value: 'Mein Büro',
    title: 'Ort oder Link für alle Termine — z.B. „Café Adler" oder „https://zoom.us/j/...". Wird bei den Teilnehmern und im Kalender angezeigt. URLs werden als klickbarer Link dargestellt.',
    maxlength: 200,
  }) as HTMLInputElement;

  const titleRow = el('div', { class: 'share-title-row' }, titleInput, locationInput);

  // Vote-Cap-Einsteller: Default 50, Range 1-500. Bei „voll" akzeptiert der
  // Server keine neuen Voter mehr (siehe security.html §4 + §5.2).
  const voteCapInput = el('input', {
    type: 'number',
    class: 'share-cap-input',
    min: '1',
    max: '500',
    step: '1',
    value: '50',
  }) as HTMLInputElement;
  const voteCapLabel = el('label', { class: 'share-cap-label' },
    el('span', {}, 'Maximale Teilnehmer'),
    voteCapInput,
    el('span', { class: 'share-cap-hint' }, '1–500. Spam-Schutz: bei Erreichen werden keine neuen Stimmen akzeptiert.'),
  );

  // Abstimmungs-Deadline (optional). Custom Datum-Button (gleicher 3-Monats-
  // Picker wie oben in der Config) + Native Time-Input. Konsistent mit dem
  // Builder-Datums-Picker.
  // State: ISO-Datum + HH:MM separat. Vorgefüllt wird automatisch auf
  // 4 Tage vor dem frühesten Termin (Default-Annahme: Admin will mit dem
  // Ergebnis 4 Tage Puffer haben).
  let deadlineDate: string | null = null;
  let deadlineTime = '18:00';
  const deadlineDateBtn = el('button',
    { type: 'button', class: 'input-date input-date-button share-deadline-date' },
    '— Datum wählen',
  ) as HTMLButtonElement;
  const deadlineTimeInput = el('input', {
    type: 'time',
    class: 'share-deadline-time',
    value: deadlineTime,
  }) as HTMLInputElement;
  const deadlineClearBtn = el('button',
    { type: 'button', class: 'share-deadline-clear', title: 'Deadline entfernen' },
    '×',
  ) as HTMLButtonElement;
  deadlineClearBtn.style.display = 'none';

  const renderDeadlineButton = () => {
    if (deadlineDate === null) {
      deadlineDateBtn.textContent = '— Datum wählen';
      deadlineClearBtn.style.display = 'none';
    } else {
      deadlineDateBtn.textContent = formatDateShort(deadlineDate);
      deadlineClearBtn.style.display = '';
    }
  };
  deadlineDateBtn.addEventListener('click', () => {
    openDateRangePicker({
      value: deadlineDate ?? defaultDeadlineDate(store.get().appointments) ?? todayIso(),
      anchor: deadlineDateBtn,
      onPick: (iso) => {
        deadlineDate = iso;
        renderDeadlineButton();
      },
    });
  });
  deadlineTimeInput.addEventListener('change', () => {
    deadlineTime = deadlineTimeInput.value || '18:00';
  });
  deadlineClearBtn.addEventListener('click', () => {
    deadlineDate = null;
    renderDeadlineButton();
  });

  const deadlineRow = el('div', { class: 'share-deadline-row' },
    deadlineDateBtn, deadlineTimeInput, deadlineClearBtn,
  );
  const deadlineLabel = el('label', { class: 'share-deadline-label' },
    el('span', {}, 'Abstimmungs-Deadline (optional)'),
    deadlineRow,
    el('span', { class: 'share-deadline-hint' },
      'Nach diesem Zeitpunkt akzeptiert der Server keine neuen Stimmen mehr. ',
      'Voter sehen einen Countdown. ',
      el('em', {}, 'Vorausgefüllt: 4 Tage vor dem frühesten Termin.'),
    ),
  );

  // Voter-Visibility: was sehen die Teilnehmer voneinander?
  // Default: aggregate (anonyme Counts pro Slot) — der typische Fall.
  // Pillen statt Dropdown — ein Klick reicht, keine Liste aufpoppen lassen.
  let currentVisibility: VoterVisibility = 'aggregate';
  const visibilityOptions: Array<{ value: VoterVisibility; label: string; sub: string; title: string }> = [
    { value: 'aggregate', label: 'Anonyme Aggregate', sub: 'Anzahl pro Slot ✓ ? ✗',
      title: 'Pro Slot Anzahl ✓ / ? / ✗ — keine Pseudonyme. Default.' },
    { value: 'full',      label: 'Alle Antworten',    sub: 'Pseudonyme sichtbar',
      title: 'Voter sehen Pseudonyme + deren Stimmen.' },
    { value: 'none',      label: 'Nichts',            sub: 'nur eigene Stimme',
      title: 'Voter sehen nur ihre eigene Antwort.' },
  ];
  const visibilityPills = el('div', { class: 'pills share-visibility-pills' });
  const visibilityButtons = new Map<VoterVisibility, HTMLButtonElement>();
  for (const opt of visibilityOptions) {
    const btn = el('button', {
      type: 'button',
      class: 'pill share-visibility-pill' + (opt.value === currentVisibility ? ' is-selected' : ''),
      title: opt.title,
      'data-vis': opt.value,
    },
      el('span', { class: 'share-visibility-pill-label' }, opt.label),
      el('span', { class: 'share-visibility-pill-sub' }, opt.sub),
    ) as HTMLButtonElement;
    btn.addEventListener('click', () => {
      currentVisibility = opt.value;
      for (const [v, b] of visibilityButtons) b.classList.toggle('is-selected', v === opt.value);
    });
    visibilityButtons.set(opt.value, btn);
    visibilityPills.append(btn);
  }
  const setVisibility = (v: VoterVisibility) => {
    currentVisibility = v;
    for (const [val, b] of visibilityButtons) b.classList.toggle('is-selected', val === v);
  };
  const visibilityLabel = el('div', { class: 'share-visibility-label' },
    el('span', { class: 'share-visibility-heading' }, 'Was sehen die Teilnehmer?'),
    visibilityPills,
    el('span', { class: 'share-visibility-hint' },
      'Kann später im Admin-Bereich geändert werden.'),
  );

  const primaryBtn = el(
    'button',
    { type: 'button', class: 'btn-primary share-create-btn' },
    'Umfrage erstellen & teilen',
  ) as HTMLButtonElement;

  // Edit-Banner: zeigt im EDIT-Modus an, welche Umfrage gerade bearbeitet
  // wird, plus „Modal nochmal anzeigen" und „Neue Umfrage anlegen".
  const editBanner = el('div', { class: 'share-edit-banner' });
  editBanner.style.display = 'none';
  const editBannerText = el('span', { class: 'share-edit-banner-text' });
  const showModalBtn = el('button', {
    type: 'button', class: 'share-edit-banner-link',
  }, 'Teilen-Modal anzeigen') as HTMLButtonElement;
  const resetBtn = el('button', {
    type: 'button', class: 'share-edit-banner-link share-edit-banner-reset',
    title: 'Aktuelle Umfrage loslassen und eine neue Umfrage anlegen — alte Umfrage existiert weiter, ihren Voter-Link kannst du im Admin-Dashboard wiederfinden.',
  }, 'Neue leere Umfrage') as HTMLButtonElement;
  editBanner.append(editBannerText, ' · ', showModalBtn, ' · ', resetBtn);

  const errorEl = el('p', { class: 'share-error', role: 'alert' });
  errorEl.style.display = 'none';

  // ─── Modus-Verwaltung ────────────────────────────────────────────────
  let currentBuild: CurrentBuild | null = getCurrentBuild();

  const updateButtonMode = () => {
    const s = store.get();
    if (currentBuild) {
      primaryBtn.textContent = '✏ Änderungen übernehmen';
      primaryBtn.classList.add('is-edit-mode');
      editBanner.style.display = '';
      editBannerText.textContent = '✓ Umfrage erstellt. Änderungen am Titel, Ort, Vote-Cap oder an den Slots werden übernommen, ohne den Voter-Link zu ändern.';
    } else {
      primaryBtn.textContent = 'Umfrage erstellen & teilen';
      primaryBtn.classList.remove('is-edit-mode');
      editBanner.style.display = 'none';
    }
    primaryBtn.disabled = s.appointments.length === 0;
  };

  const showError = (msg: string) => {
    errorEl.textContent = msg;
    errorEl.style.display = 'block';
  };
  const clearError = () => { errorEl.style.display = 'none'; };

  // ─── Modal-Hilfsfunktion ─────────────────────────────────────────────
  const openShareModal = (
    args: {
      id: string; adminToken: string; voterToken: string;
      expiresAt: number | null; voteCap: number | null;
      title: string | null; location: string | null;
      slots: import('../types').Appointment[];
      config: import('../types').FinderConfig;
    },
  ): void => {
    showCreateModal({
      result: {
        id: args.id,
        admin_token: args.adminToken,
        voter_token: args.voterToken,
        expires_at: args.expiresAt,
        vote_cap: args.voteCap,
      },
      title: args.title,
      config: args.config,
      slots: args.slots,
      origin: window.location.origin,
      location: args.location,
    });
  };

  // ─── Click-Handler ───────────────────────────────────────────────────
  primaryBtn.addEventListener('click', async () => {
    const s = store.get();
    if (s.appointments.length === 0) return;

    primaryBtn.disabled = true;
    const orig = primaryBtn.textContent;
    primaryBtn.textContent = currentBuild ? 'Aktualisiere …' : 'Wird erstellt …';
    clearError();

    try {
      const title = titleInput.value.trim() || null;
      const location = locationInput.value.trim() || null;
      const cap = Number(voteCapInput.value);
      const voteCap = Number.isFinite(cap) && cap >= 1 && cap <= 500 ? Math.round(cap) : 50;
      const visibility = currentVisibility;
      const votingDeadline = deadlineDate
        ? parseDateTimeToUnix(deadlineDate, deadlineTime || '18:00')
        : null;
      if (votingDeadline !== null && votingDeadline <= Math.floor(Date.now() / 1000) && !currentBuild) {
        showError('Deadline muss in der Zukunft liegen.');
        return;
      }

      if (currentBuild) {
        // ── EDIT ─────────────────────────────────────────────────────
        // Vorab den Server-Stand laden, um Voter-Lösch-Warnung zu zeigen.
        const fresh = await getAdminSurvey(currentBuild.adminToken);
        if (fresh.votes.length > 0) {
          const oldSlotIds = new Set(fresh.slots.map((sl) => sl.id));
          const newSlotIds = new Set(s.appointments.map((a) => a.id));
          const removedIds = [...oldSlotIds].filter((id) => !newSlotIds.has(id));
          if (removedIds.length > 0) {
            let affected = 0;
            for (const v of fresh.votes) {
              for (const id of removedIds) {
                if (v.responses[id]) affected++;
              }
            }
            if (affected > 0) {
              const ok = window.confirm(
                `Achtung: ${affected} bereits abgegebene Stimme${affected === 1 ? '' : 'n'} ` +
                `betreffen Slots, die du jetzt entfernst. Diese Stimmen gehen verloren. ` +
                `Trotzdem speichern?`,
              );
              if (!ok) return;
            }
          }
        }

        await updateSurvey(currentBuild.adminToken, {
          title, location,
          slots: s.appointments,
          config: s.config,
          vote_cap: voteCap,
          voter_results_visibility: visibility,
          voting_deadline: votingDeadline,
        });

        // Neuer Stand vom Server (für expires_at im Modal).
        const after = await getAdminSurvey(currentBuild.adminToken);
        openShareModal({
          id: after.id,
          adminToken: after.admin_token,
          voterToken: after.voter_token,
          expiresAt: after.expires_at,
          voteCap: voteCap,
          title: after.title,
          location: after.location,
          slots: after.slots,
          config: after.config,
        });
      } else {
        // ── CREATE ───────────────────────────────────────────────────
        const result = await createSurvey({
          title: title ?? undefined,
          config: s.config,
          slots: s.appointments,
          vote_cap: voteCap,
          voter_results_visibility: visibility,
          location,
          voting_deadline: votingDeadline,
        });

        // In das lokale Dashboard aufnehmen — Privacy-First: nur localStorage.
        addSurvey({
          id: result.id,
          title,
          adminToken: result.admin_token,
          voterToken: result.voter_token,
          createdAt: Math.floor(Date.now() / 1000),
          expiresAt: result.expires_at,
        });

        // Edit-Modus aktivieren.
        currentBuild = {
          id: result.id,
          adminToken: result.admin_token,
          voterToken: result.voter_token,
          voteCount: 0,
        };
        setCurrentBuild(currentBuild);

        openShareModal({
          id: result.id,
          adminToken: result.admin_token,
          voterToken: result.voter_token,
          expiresAt: result.expires_at,
          voteCap: result.vote_cap,
          title, location,
          slots: s.appointments,
          config: s.config,
        });
      }
    } catch (e) {
      const message = e instanceof ApiError
        ? `Fehler vom Server (${e.status}): ${e.message}`
        : e instanceof Error
          ? `Konnte Umfrage ${currentBuild ? 'nicht aktualisieren' : 'nicht erstellen'}: ${e.message}`
          : 'Unbekannter Fehler.';
      showError(message);
    } finally {
      primaryBtn.textContent = orig ?? '';
      updateButtonMode();
    }
  });

  // „Teilen-Modal anzeigen" — frischen Stand vom Server holen und Modal öffnen.
  showModalBtn.addEventListener('click', async () => {
    if (!currentBuild) return;
    showModalBtn.disabled = true;
    const orig = showModalBtn.textContent;
    showModalBtn.textContent = '…';
    try {
      const after = await getAdminSurvey(currentBuild.adminToken);
      openShareModal({
        id: after.id,
        adminToken: after.admin_token,
        voterToken: after.voter_token,
        expiresAt: after.expires_at,
        voteCap: null,
        title: after.title,
        location: after.location,
        slots: after.slots,
        config: after.config,
      });
    } catch (e) {
      showError(e instanceof Error ? e.message : 'Konnte Umfrage nicht laden.');
    } finally {
      showModalBtn.disabled = false;
      showModalBtn.textContent = orig ?? 'Teilen-Modal anzeigen';
    }
  });

  // „Neue leere Umfrage" — Edit-Bindung lösen, alte Umfrage bleibt im Server
  // und im /admin-Dashboard. User kann jetzt eine zweite separate Umfrage
  // anlegen.
  resetBtn.addEventListener('click', () => {
    const ok = window.confirm(
      'Bindung zur aktuellen Umfrage lösen? Die alte Umfrage bleibt erhalten ' +
      '(zu finden im Admin-Dashboard). Beim nächsten Erstellen entsteht ' +
      'eine komplett neue Umfrage mit eigenen Tokens.',
    );
    if (!ok) return;
    currentBuild = null;
    clearCurrentBuild();
    clearError();
    updateButtonMode();
  });

  root.append(
    heading, intro, privacyNote, titleRow,
    voteCapLabel, deadlineLabel, visibilityLabel, primaryBtn, editBanner, errorEl,
  );

  store.subscribe(() => {
    updateButtonMode();
  });
  updateButtonMode();

  // Erste Vorbefüllung der Deadline auf „4 Tage vor frühestem Termin",
  // sobald der Store mind. einen Termin hat. Sich nicht selbst überschreiben,
  // falls der User schon was eingestellt hat.
  let deadlineUserTouched = false;
  deadlineDateBtn.addEventListener('click', () => { deadlineUserTouched = true; }, { capture: true });
  deadlineClearBtn.addEventListener('click', () => { deadlineUserTouched = true; }, { capture: true });
  store.subscribe((s) => {
    if (deadlineUserTouched || deadlineDate !== null) return;
    const auto = defaultDeadlineDate(s.appointments);
    if (auto) {
      deadlineDate = auto;
      renderDeadlineButton();
    }
  });

  // Hydration: gibt's eine bestehende Umfrage und ist die Matrix leer
  // (z.B. nach Reload), Inhalte vom Server holen und in den Builder laden.
  // So kann der User direkt weiter editieren.
  //
  // Sonderfall: Survey ist bereits finalisiert oder geschlossen → Edit-Bindung
  // lösen, damit der User nicht versehentlich versucht, eine fertige Umfrage
  // weiter zu bearbeiten. Eine fertige Umfrage soll im Admin-Bereich aufgerufen
  // werden, nicht im Builder.
  if (currentBuild && store.get().appointments.length === 0) {
    void hydrateFromServer(currentBuild, store, {
      titleInput, locationInput, voteCapInput, setVisibility,
      setDeadline: (unix) => {
        const dt = unixToDateTime(unix);
        deadlineDate = dt?.date ?? null;
        deadlineTime = dt?.time ?? deadlineTime;
        deadlineTimeInput.value = deadlineTime;
        deadlineUserTouched = true;
        renderDeadlineButton();
      },
      onFinalizedOrClosed: () => {
        // Wir kommen hier raus, ohne Builder-State zu seedem. Dadurch sieht
        // der User eine leere/fresh Builder-Seite — genau was er will.
        currentBuild = null;
        clearCurrentBuild();
        updateButtonMode();
      },
    }).catch((e) => {
      // Ist die Umfrage z.B. abgelaufen oder gelöscht, Edit-Bindung lösen.
      if (e instanceof ApiError && (e.status === 404 || e.status === 410)) {
        currentBuild = null;
        clearCurrentBuild();
        updateButtonMode();
      }
    });
  }

  return root;
}

/** Lädt die bestehende Umfrage in Builder + Share-Panel-Inputs. */
async function hydrateFromServer(
  build: CurrentBuild,
  store: Store,
  inputs: {
    titleInput: HTMLInputElement;
    locationInput: HTMLInputElement;
    voteCapInput: HTMLInputElement;
    setVisibility: (v: VoterVisibility) => void;
    setDeadline: (unix: number | null) => void;
    onFinalizedOrClosed: () => void;
  },
): Promise<void> {
  const survey = await getAdminSurvey(build.adminToken);
  // Finalisiert oder geschlossen → der Builder ist hier nicht mehr der
  // richtige Ort. Edit-Bindung lösen und Builder leer lassen.
  const isFinalized = (survey.final_slot_ids?.length ?? 0) > 0;
  const isClosed = survey.closed_at !== null;
  if (isFinalized || isClosed) {
    inputs.onFinalizedOrClosed();
    return;
  }
  store.setConfig(survey.config);
  store.setAppointments(survey.slots);
  inputs.titleInput.value = survey.title ?? '';
  inputs.locationInput.value = survey.location ?? '';
  if (survey.vote_cap !== null) inputs.voteCapInput.value = String(survey.vote_cap);
  inputs.setVisibility(survey.voter_results_visibility);
  inputs.setDeadline(survey.voting_deadline);
}

/** ISO-Datum + HH:MM → Unix-Sekunden in lokaler Browser-TZ. */
function parseDateTimeToUnix(date: string, time: string): number | null {
  const ms = new Date(`${date}T${time}`).getTime();
  if (!Number.isFinite(ms)) return null;
  return Math.floor(ms / 1000);
}

/** Unix-Sekunden → { date: ISO, time: HH:MM } in lokaler TZ. null bei null. */
function unixToDateTime(unix: number | null): { date: string; time: string } | null {
  if (unix === null) return null;
  const d = new Date(unix * 1000);
  const pad = (n: number) => String(n).padStart(2, '0');
  return {
    date: `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`,
    time: `${pad(d.getHours())}:${pad(d.getMinutes())}`,
  };
}

/** ISO heute, lokal. */
function todayIso(): string {
  const d = new Date();
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

/** 4 Tage vor dem frühesten Termin als ISO. null wenn keine Termine. Wenn
 *  4 Tage davor schon vorbei sind (Termin bald), wir geben „heute" zurück. */
function defaultDeadlineDate(appointments: import('../types').Appointment[]): string | null {
  if (appointments.length === 0) return null;
  const earliest = appointments.reduce((min, a) => (a.date < min ? a.date : min), appointments[0].date);
  const d = new Date(`${earliest}T00:00:00`);
  d.setDate(d.getDate() - 4);
  const pad = (n: number) => String(n).padStart(2, '0');
  const iso = `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
  const today = todayIso();
  return iso < today ? today : iso;
}
